import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Avatar,
  IconButton,
  Chip,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Alert
} from '@mui/material';
import {
  Send as SendIcon,
  ThumbUpOutlined as ThumbUpOutlinedIcon,
  Reply as ReplyIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Comment as CommentIcon
} from '@mui/icons-material';
import { TopicComment } from '../types/maintenanceBank';
import { TopicCommentsService } from '../services/topicCommentsService';
import { useAuth } from '../contexts/AuthContext';

interface TopicCommentsProps {
  topicId: string;
}

const TopicComments: React.FC<TopicCommentsProps> = ({ topicId }) => {
  const { currentUser } = useAuth();
  const [comments, setComments] = useState<TopicComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedComment, setSelectedComment] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // تحميل التعليقات
  useEffect(() => {
    loadComments();
  }, [topicId]);

  const loadComments = async () => {
    try {
      setLoading(true);
      const data = await TopicCommentsService.getCommentsByTopic(topicId);
      setComments(data);
    } catch (error: any) {
      console.error('خطأ في تحميل التعليقات:', error);
      setError('فشل في تحميل التعليقات');
    } finally {
      setLoading(false);
    }
  };

  // إضافة تعليق جديد
  const handleAddComment = async () => {
    if (!newComment.trim() || !currentUser) return;

    try {
      await TopicCommentsService.addComment({
        topicId,
        content: newComment,
        authorId: currentUser.id || 'unknown',
        authorName: currentUser.name || currentUser.email || 'مستخدم',
        authorRole: (currentUser.role as any) || 'user',
        isEdited: false,
        likes: 0
      });

      setNewComment('');
      loadComments();
    } catch (error: any) {
      console.error('خطأ في إضافة التعليق:', error);
      setError('فشل في إضافة التعليق');
    }
  };

  // إضافة رد
  const handleAddReply = async (parentId: string) => {
    if (!replyContent.trim() || !currentUser) return;

    try {
      await TopicCommentsService.addComment({
        topicId,
        parentId,
        content: replyContent,
        authorId: currentUser.id || 'unknown',
        authorName: currentUser.name || currentUser.email || 'مستخدم',
        authorRole: (currentUser.role as any) || 'user',
        isEdited: false,
        likes: 0
      });

      setReplyContent('');
      setReplyTo(null);
      loadComments();
    } catch (error: any) {
      console.error('خطأ في إضافة الرد:', error);
      setError('فشل في إضافة الرد');
    }
  };

  // تعديل تعليق
  const handleEditComment = async (commentId: string) => {
    if (!editContent.trim()) return;

    try {
      await TopicCommentsService.updateComment(commentId, editContent);
      setEditingComment(null);
      setEditContent('');
      loadComments();
    } catch (error: any) {
      console.error('خطأ في تعديل التعليق:', error);
      setError('فشل في تعديل التعليق');
    }
  };

  // حذف تعليق
  const handleDeleteComment = async (commentId: string) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا التعليق؟')) return;

    try {
      await TopicCommentsService.deleteComment(commentId);
      loadComments();
    } catch (error: any) {
      console.error('خطأ في حذف التعليق:', error);
      setError('فشل في حذف التعليق');
    }
  };

  // إعجاب بتعليق
  const handleLikeComment = async (commentId: string, currentLikes: number) => {
    try {
      await TopicCommentsService.likeComment(commentId, currentLikes);
      loadComments();
    } catch (error: any) {
      console.error('خطأ في الإعجاب:', error);
    }
  };

  // فتح قائمة الخيارات
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, commentId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedComment(commentId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedComment(null);
  };

  // تصفية التعليقات الرئيسية
  const mainComments = comments.filter(c => !c.parentId);

  // الحصول على ردود تعليق
  const getReplies = (commentId: string) => {
    return comments.filter(c => c.parentId === commentId);
  };

  // الحصول على لون الدور
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'error';
      case 'technician': return 'primary';
      case 'employee': return 'secondary';
      default: return 'default';
    }
  };

  // الحصول على نص الدور
  const getRoleText = (role: string) => {
    switch (role) {
      case 'admin': return 'مدير';
      case 'technician': return 'فني';
      case 'employee': return 'موظف';
      default: return 'مستخدم';
    }
  };

  // عرض تعليق واحد
  const renderComment = (comment: TopicComment, isReply: boolean = false) => {
    const isAuthor = currentUser?.id === comment.authorId;
    const isAdmin = currentUser?.role === 'ADMIN';
    const canEdit = isAuthor;
    const canDelete = isAuthor || isAdmin;

    return (
      <Paper
        key={comment.id}
        sx={{
          p: 2,
          mb: 2,
          ml: isReply ? 4 : 0,
          bgcolor: isReply ? 'grey.50' : 'background.paper',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        {/* رأس التعليق */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
              {comment.authorName[0]?.toUpperCase()}
            </Avatar>
            <Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {comment.authorName}
                </Typography>
                <Chip
                  label={getRoleText(comment.authorRole)}
                  size="small"
                  color={getRoleColor(comment.authorRole) as any}
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {comment.createdAt?.toLocaleDateString('ar-EG', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                {comment.isEdited && ' (معدّل)'}
              </Typography>
            </Box>
          </Box>

          {(canEdit || canDelete) && (
            <IconButton size="small" onClick={(e) => handleMenuOpen(e, comment.id)}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* محتوى التعليق */}
        {editingComment === comment.id ? (
          <Box>
            <TextField
              fullWidth
              multiline
              rows={2}
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              sx={{ mb: 1 }}
              autoFocus
            />
            <Box display="flex" gap={1}>
              <Button 
                size="small" 
                variant="contained" 
                onClick={() => handleEditComment(comment.id)}
                disabled={!editContent.trim()}
              >
                حفظ
              </Button>
              <Button 
                size="small" 
                onClick={() => {
                  setEditingComment(null);
                  setEditContent('');
                }}
              >
                إلغاء
              </Button>
            </Box>
          </Box>
        ) : (
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 1, 
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}
          >
            {comment.content}
          </Typography>
        )}

        {/* أزرار التفاعل */}
        {editingComment !== comment.id && (
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              size="small"
              startIcon={<ThumbUpOutlinedIcon />}
              onClick={() => handleLikeComment(comment.id, comment.likes)}
              sx={{ minWidth: 'auto' }}
            >
              {comment.likes || 0}
            </Button>
            <Button
              size="small"
              startIcon={<ReplyIcon />}
              onClick={() => {
                setReplyTo(comment.id);
                setReplyContent('');
              }}
            >
              رد
            </Button>
          </Box>
        )}

        {/* حقل الرد */}
        {replyTo === comment.id && (
          <Box mt={2}>
            <TextField
              fullWidth
              multiline
              rows={2}
              placeholder="اكتب ردك..."
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              sx={{ mb: 1 }}
              autoFocus
            />
            <Box display="flex" gap={1}>
              <Button
                size="small"
                variant="contained"
                startIcon={<SendIcon />}
                onClick={() => handleAddReply(comment.id)}
                disabled={!replyContent.trim()}
              >
                إرسال
              </Button>
              <Button 
                size="small" 
                onClick={() => {
                  setReplyTo(null);
                  setReplyContent('');
                }}
              >
                إلغاء
              </Button>
            </Box>
          </Box>
        )}

        {/* عرض الردود */}
        {getReplies(comment.id).map(reply => renderComment(reply, true))}
      </Paper>
    );
  };

  return (
    <Box mt={4}>
      <Typography 
        variant="h5" 
        gutterBottom 
        sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}
      >
        <CommentIcon />
        التعليقات ({comments.length})
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* إضافة تعليق جديد */}
      {currentUser ? (
        <Paper sx={{ p: 2, mb: 3 }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="أضف تعليقك..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleAddComment}
            disabled={!newComment.trim() || loading}
          >
            إرسال التعليق
          </Button>
        </Paper>
      ) : (
        <Alert severity="info" sx={{ mb: 3 }}>
          يجب تسجيل الدخول لإضافة تعليق
        </Alert>
      )}

      {/* عرض التعليقات */}
      {loading && comments.length === 0 ? (
        <Box textAlign="center" py={4}>
          <Typography color="text.secondary">جاري التحميل...</Typography>
        </Box>
      ) : mainComments.length > 0 ? (
        mainComments.map(comment => renderComment(comment))
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            لا توجد تعليقات بعد. كن أول من يعلق!
          </Typography>
        </Paper>
      )}

      {/* قائمة الخيارات */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          const comment = comments.find(c => c.id === selectedComment);
          if (comment) {
            setEditingComment(comment.id);
            setEditContent(comment.content);
          }
          handleMenuClose();
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>تعديل</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedComment) {
            handleDeleteComment(selectedComment);
          }
          handleMenuClose();
        }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>حذف</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default TopicComments;
